import * as Sentry from '@sentry/react';
import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  FileImage, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Camera,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CounterSubmissionPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [requestInfo, setRequestInfo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchRequestInfo();
  }, [token]);

  const fetchRequestInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/counter-request/${token}`);
      setRequestInfo(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Ce relevé a déjà été soumis ou la période est expirée.');
      } else if (err.response?.status === 404) {
        setError('Ce lien est invalide ou a expiré.');
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner une photo ou un PDF');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(`${API_URL}/api/public/counter-request/${token}/submit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSubmitted(true);
      toast.success('Votre relevé a été envoyé avec succès !');
    } catch (err) {
      Sentry.captureException(err);
      if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Lien invalide</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Merci !</h2>
            <p className="text-muted-foreground mb-4">
              Votre relevé de compteur a été envoyé avec succès.
            </p>
            <p className="text-sm text-muted-foreground">
              Notre équipe va traiter votre soumission dans les plus brefs délais.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Camera className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Relevé de compteur</h1>
          <p className="text-muted-foreground">Soumettez une photo de votre compteur</p>
        </div>

        {/* Equipment Info Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Équipement concerné</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-semibold text-lg">{requestInfo?.equipment_info}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Période : {new Date(requestInfo?.period_start).toLocaleDateString('fr-FR')} - {new Date(requestInfo?.period_end).toLocaleDateString('fr-FR')}
              </p>
            </div>

            {/* Protocol Instructions */}
            {requestInfo?.protocol_compteur && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Comment lire votre compteur
                </h4>
                <p className="text-sm text-amber-700 whitespace-pre-line">
                  {requestInfo.protocol_compteur}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Envoyer une photo</CardTitle>
            <CardDescription>
              Prenez une photo claire de l'écran du compteur ou téléchargez un PDF
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Dropzone */}
            {!selectedFile ? (
              <div
                {...getRootProps()}
                className={`
                  relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                  ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary/50 hover:bg-primary/5/50'}
                  ${isDragReject ? 'border-red-500 bg-red-50' : ''}
                `}
                data-testid="dropzone"
              >
                <input {...getInputProps()} data-testid="file-input" />
                <div className="flex flex-col items-center gap-4">
                  <div className={`
                    w-16 h-16 rounded-full flex items-center justify-center transition-colors
                    ${isDragActive ? 'bg-primary/10' : 'bg-gray-100'}
                  `}>
                    <Upload className={`w-8 h-8 ${isDragActive ? 'text-primary' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {isDragActive ? 'Déposez votre fichier ici' : 'Glissez-déposez votre fichier'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      ou <span className="text-primary font-medium">parcourir</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-3">
                      JPG, PNG ou PDF • Max 10 Mo
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border rounded-xl p-4">
                {/* File Preview */}
                {preview && (
                  <div className="mb-4 relative">
                    <img
                      src={preview}
                      alt="Aperçu"
                      className="w-full h-48 object-contain rounded-lg bg-gray-100"
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      {selectedFile.type.startsWith('image/') ? (
                        <FileImage className="w-5 h-5 text-primary" />
                      ) : (
                        <FileText className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} Mo
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeFile}
                    className="text-gray-400 hover:text-red-500"
                    data-testid="remove-file-btn"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!selectedFile || submitting}
              className="w-full mt-6 bg-primary hover:bg-primary/90 h-12 text-base"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Envoyer mon relevé
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              En soumettant ce formulaire, vous confirmez que les informations sont exactes.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Hélios • Gestion de facturation
        </p>
      </div>
    </div>
  );
};

export default CounterSubmissionPage;
